import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Card,
  Image as AntImage,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd'
import { AssetVizMode } from 'features/assetViz'
import {
  CommercialFdeCdfSpecificationList,
  useCdfDataFetcher,
  useInitializeDerivedCdfsFromRolodexData,
} from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde'
import { AssetVizCopy } from 'pages/reporting/components/AssetVizCopy'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { ImagePageHeader } from 'pages/reporting/reports/rug/components/ImagePageHeader'
import {
  LAYOUT,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import { captureAssetScreenshot } from 'pages/reporting/reports/rug/utils/captureAssetScreenshot'
import { ReportPage } from 'pages/reporting/shared/pdfGeneration/components/shared/ReportPage'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentShape,
  GeckoFeature,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  isInspectionThicknessCDF,
} from 'types'
import { associateBy } from 'utils/fde/maps'

const { Text: AntText } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const ComponentSelector = styled.div`
  margin-bottom: 1rem;
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ImageCard = styled(Card)`
  margin-bottom: 1rem;

  .ant-image {
    background-color: #ffffff;
  }
`

interface ComponentScreenshot {
  componentId: string
  componentName: string
  signedUrl: string
  imagePath: string
}

interface ComponentDrawing {
  componentId: string
  componentName: string
  signedUrl: string
  imagePath: string
  extension: string
}

// PDF Styles matching the old component
const pageStyles = StyleSheet.create({
  imagePage: {
    paddingTop: SPACING.PADDING.SMALL,
    paddingBottom: SPACING.PADDING.PAGE_BOTTOM,
    paddingHorizontal: SPACING.PADDING.LARGE,
    fontFamily: TYPOGRAPHY.FONT_FAMILY,
  },
  imageHeaderItem: {
    fontSize: TYPOGRAPHY.FONT_SIZE.EXTRA_SMALL,
  },
  footerline: {
    border: `${LAYOUT.BORDER_WIDTH.MEDIUM} solid ${REPORT_COLORS.GECKO_GRAY}`,
  },
  pageImage: {
    maxWidth: '100%',
    maxHeight: 310,
    objectFit: 'contain',
  },
})

// Helper Components
const PageSubheader: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignContent: 'center',
        marginBottom: SPACING.MARGIN.MEDIUM,
      }}>
      <View style={{ flexDirection: 'column' }}>
        <View
          style={{
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            margin: `${SPACING.MARGIN.SMALL} ${SPACING.MARGIN.MEDIUM} ${SPACING.MARGIN.EXTRA_SMALL} 0`,
            width: LAYOUT.WIDTH.DECORATIVE_MEDIUM,
            backgroundColor: REPORT_COLORS.LIGHT_GRAY,
          }}
        />
        <View
          style={{
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            margin: `${SPACING.MARGIN.SMALL} ${SPACING.MARGIN.HUGE} ${SPACING.MARGIN.EXTRA_SMALL} 0`,
            width: LAYOUT.WIDTH.DECORATIVE_EXTRA_SMALL,
            backgroundColor: REPORT_COLORS.LIGHT_GRAY,
          }}
        />
        <View
          style={{
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            margin: `${SPACING.MARGIN.SMALL} ${SPACING.MARGIN.LARGE + 2} ${SPACING.MARGIN.EXTRA_SMALL} 0`,
            width: LAYOUT.WIDTH.DECORATIVE_SMALL,
            backgroundColor: REPORT_COLORS.LIGHT_GRAY,
          }}
        />
      </View>
      <View style={{ flexDirection: 'column' }}>
        <Text
          style={{
            fontSize: TYPOGRAPHY.FONT_SIZE.LARGE,
            lineHeight: TYPOGRAPHY.LINE_HEIGHT.NORMAL,
            fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
            color: REPORT_COLORS.DARKER_GRAY,
          }}>
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{
              fontSize: TYPOGRAPHY.FONT_SIZE.MEDIUM,
              lineHeight: TYPOGRAPHY.LINE_HEIGHT.NORMAL,
              fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
              color: REPORT_COLORS.GECKO_BLUE_GREEN,
              marginTop: 2,
            }}>
            {subtitle}
          </Text>
        )}
      </View>
    </View>
  )
}

const PageHeader: React.FC<{ title: string }> = ({ title }) => {
  return (
    <View
      style={{
        backgroundColor: REPORT_COLORS.BLACK,
        height: LAYOUT.HEIGHT.PAGE_HEADER,
        position: 'relative',
        flexDirection: 'row',
        gap: SPACING.GAP.MEDIUM,
        alignItems: 'center',
        marginBottom: SPACING.MARGIN.EXTRA_LARGE,
      }}>
      <Text
        style={{
          textAlign: 'right',
          width: '100%',
          fontSize: TYPOGRAPHY.FONT_SIZE.TITLE,
          lineHeight: TYPOGRAPHY.LINE_HEIGHT.TIGHT,
          fontWeight: TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD,
          color: REPORT_COLORS.WHITE,
          flexGrow: 1,
        }}>
        {title}
      </Text>
      <View
        style={{
          height: '100%',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexGrow: 0,
          margin: `${SPACING.MARGIN.EXTRA_SMALL} 0`,
        }}>
        <View
          style={{
            width: LAYOUT.WIDTH.DECORATIVE_LARGE,
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            backgroundColor: REPORT_COLORS.WHITE,
            margin: `${SPACING.MARGIN.EXTRA_SMALL} 0`,
          }}
        />
        <View
          style={{
            width: LAYOUT.WIDTH.DECORATIVE_EXTRA_SMALL,
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            backgroundColor: REPORT_COLORS.WHITE,
            margin: `${SPACING.MARGIN.EXTRA_SMALL} 0`,
          }}
        />
        <View
          style={{
            width: LAYOUT.WIDTH.DECORATIVE_SMALL,
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            backgroundColor: REPORT_COLORS.WHITE,
            margin: `${SPACING.MARGIN.EXTRA_SMALL} 0`,
          }}
        />
      </View>
    </View>
  )
}

const PageFooter: React.FC<{
  jobNumber: string
  revisionNumber?: number
  sectionNumber?: number
}> = ({ jobNumber, revisionNumber, sectionNumber }) => {
  return (
    <>
      <View
        style={{
          position: 'absolute',
          bottom: LAYOUT.POSITION.FOOTER_BOTTOM,
          width: LAYOUT.WIDTH.FULL,
        }}>
        <View
          style={{
            borderBottom: `${LAYOUT.BORDER_WIDTH.MEDIUM} solid ${REPORT_COLORS.DARK_GRAY}`,
            width: LAYOUT.WIDTH.FULL,
          }}
        />
      </View>
      <Text
        style={{
          position: 'absolute',
          fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
          bottom: LAYOUT.POSITION.FOOTER_TEXT_BOTTOM,
          left: LAYOUT.POSITION.FOOTER_SIDE_OFFSET,
          color: REPORT_COLORS.GECKO_GRAY,
          fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
        }}
        render={() => `Job Number: ${jobNumber}`}
      />
      <View
        style={{
          position: 'absolute',
          fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
          bottom: LAYOUT.POSITION.FOOTER_TEXT_BOTTOM,
          left: 0,
          right: 0,
          flexDirection: 'row',
          justifyContent: 'center',
          color: REPORT_COLORS.GECKO_GRAY,
          fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
        }}>
        {sectionNumber && (
          <>
            <Text style={{ color: REPORT_COLORS.GECKO_GRAY }}>
              Section Number: {sectionNumber.toFixed(1)}{' '}
            </Text>
            <Text
              style={{
                color: REPORT_COLORS.GECKO_GRAY,
                marginHorizontal: SPACING.MARGIN.SMALL,
              }}>
              |
            </Text>
          </>
        )}
        <Text style={{ color: REPORT_COLORS.GECKO_GRAY }}>
          Report Revision: {revisionNumber ?? ''}
        </Text>
      </View>
      <Text
        style={{
          position: 'absolute',
          fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
          bottom: LAYOUT.POSITION.FOOTER_TEXT_BOTTOM,
          right: LAYOUT.POSITION.FOOTER_SIDE_OFFSET,
          color: REPORT_COLORS.GECKO_GRAY,
        }}
        render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        }
      />
    </>
  )
}

const RenderReact: FC = () => {
  const {
    selectedComponents,
    relevantCDFs,
    selectedAssetModel,
    assetViz,
    metadataEntryId,
    selectedComponentModels,
    assetScreenshots,
    forceRefreshAssetScreenshots,
    unit,
    cdfCache,
    componentDefaults,
  } = useRugReportOps()

  const { assetVizRef } = assetViz || {}
  const [selectedComponentId, setSelectedComponentId] = useState<
    ComponentId | undefined
  >()
  const [selectedCDFId, setSelectedCDFId] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const [deletingComponentId, setDeletingComponentId] =
    useState<ComponentId | null>(null)
  const [zoomFactor, setZoomFactor] = useState<number>(0.15)
  const [uploadingDrawing, setUploadingDrawing] = useState(false)
  const [deletingDrawingId, setDeletingDrawingId] =
    useState<ComponentId | null>(null)

  const [, setSelection] = useState<
    GeckoComponentShape | GeckoMarkupLayer | GeckoMarkupPrimitive | GeckoFeature
  >()

  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()

  const selectedComponent = useMemo(() => {
    if (!selectedComponentId || !selectedComponents) return undefined
    return selectedComponents.find((c) => c.id === selectedComponentId)
  }, [selectedComponentId, selectedComponents])

  const componentCDFs = useMemo(() => {
    if (!selectedComponent || !relevantCDFs) return []
    return relevantCDFs.filter(
      (cdf) => cdf.data.geometry.componentID === selectedComponent.id
    )
  }, [selectedComponent, relevantCDFs])

  const relevantCDF = useMemo(() => {
    if (!selectedComponent || componentCDFs.length === 0) return undefined
    if (selectedCDFId) {
      return componentCDFs.find((cdf) => cdf.id === selectedCDFId)
    }
    return componentCDFs[0]
  }, [selectedComponent, componentCDFs, selectedCDFId])

  useEffect(() => {
    if (selectedComponent && componentCDFs.length > 0) {
      if (
        !selectedCDFId ||
        !componentCDFs.find((cdf) => cdf.id === selectedCDFId)
      ) {
        setSelectedCDFId(componentCDFs[0].id)
      }
    } else {
      setSelectedCDFId(undefined)
    }
  }, [selectedComponent, componentCDFs, selectedCDFId])

  const colorThreshold = useMemo(() => {
    if (!relevantCDF) return undefined

    const selectedComponentDefault = componentDefaults.find(
      (cd) => cd.tags.component === selectedComponent?.id
    )

    return selectedComponentDefault?.data.color_scale_thresholds.get(
      relevantCDF.data.params.view.id
    )
  }, [relevantCDF, componentDefaults, selectedComponent?.id])

  const existingScreenshots = useMemo(() => {
    const screenshots: ComponentScreenshot[] = []

    if (assetScreenshots?.fullComponentHeatmap) {
      assetScreenshots.fullComponentHeatmap.forEach((screenshot) => {
        if (screenshot.signedUrl) {
          const component = selectedComponents?.find(
            (c) => c.id === screenshot.componentId
          )
          if (component) {
            screenshots.push({
              componentId: screenshot.componentId,
              componentName: component.data.name,
              signedUrl: screenshot.signedUrl,
              imagePath: screenshot.imagePath,
            })
          }
        }
      })
    }

    return screenshots
  }, [assetScreenshots, selectedComponents])

  const existingDrawings = useMemo(() => {
    const drawings: ComponentDrawing[] = []

    if (assetScreenshots?.fullComponentDrawing) {
      assetScreenshots.fullComponentDrawing.forEach((drawing) => {
        if (drawing.signedUrl) {
          const component = selectedComponents?.find(
            (c) => c.id === drawing.componentId
          )
          if (component) {
            drawings.push({
              componentId: drawing.componentId,
              componentName: component.data.name,
              signedUrl: drawing.signedUrl,
              imagePath: drawing.imagePath,
              extension: drawing.extension,
            })
          }
        }
      })
    }

    return drawings
  }, [assetScreenshots, selectedComponents])

  const cdfDataFetcher = useCdfDataFetcher()
  const captureComponentScreenshot = useCallback(async () => {
    if (!assetVizRef?.current || !selectedComponent || !relevantCDF) {
      message.error('Unable to capture screenshot - missing required data')
      return
    }

    const selectedComponentModel = selectedComponentModels?.find(
      (componentModel) => componentModel.id === selectedComponent.id
    )

    if (!selectedComponentModel) {
      message.error('Unable to capture screenshot - missing component model')
      return
    }

    setLoading(true)
    try {
      const currComponentBounds = selectedComponentModel.componentBounds

      // pre-load data
      // TODO: Add an event handler onto AssetViz, so we can listen for when the component is fully rendered and capture the screenshot then.
      await cdfDataFetcher(relevantCDF)

      const blob = await captureAssetScreenshot({
        assetVizRef,
        bounds: currComponentBounds,
        zoomFactor,
        // Adds a 1/2 second delay to prevent the screenshot from being captured before the component is fully rendered (e.g. color scales are being applied)
        preRenderDelay: 500,
        componentModel: selectedComponentModel,
      })

      if (blob) {
        const path = `asset-screenshot/fullComponentHeatmap/${selectedComponent.id}.png`
        await rolodexUploadFile(metadataEntryId, blob, path)

        message.success('Screenshot captured successfully')

        await forceRefreshAssetScreenshots()
      }
    } catch (error) {
      console.error('Failed to capture screenshot:', error)
      message.error('Failed to capture screenshot')
    } finally {
      setLoading(false)
    }
  }, [
    assetVizRef,
    selectedComponent,
    relevantCDF,
    selectedComponentModels,
    cdfDataFetcher,
    zoomFactor,
    rolodexUploadFile,
    metadataEntryId,
    forceRefreshAssetScreenshots,
  ])

  const handleDeleteScreenshot = useCallback(
    async (screenshot: ComponentScreenshot) => {
      setDeletingComponentId(screenshot.componentId as ComponentId)
      try {
        await rolodexDeleteFile(metadataEntryId, screenshot.imagePath)
        message.success('Screenshot deleted successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to delete screenshot:', error)
        message.error('Failed to delete screenshot')
      } finally {
        setDeletingComponentId(null)
      }
    },
    [metadataEntryId, rolodexDeleteFile, forceRefreshAssetScreenshots]
  )

  const handleDrawingUpload = useCallback(
    async (file: File) => {
      if (!selectedComponent) {
        message.error('Please select a component first')
        return
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase()

      const validExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp']
      if (!fileExtension || !validExtensions.includes(fileExtension)) {
        message.error(
          'Please upload a valid image file (png, jpg, jpeg, gif, webp)'
        )
        return
      }

      setUploadingDrawing(true)
      try {
        const path = `asset-drawing/fullComponentDrawing/${selectedComponent.id}.${fileExtension}`
        await rolodexUploadFile(metadataEntryId, file, path)
        message.success('Drawing uploaded successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to upload drawing:', error)
        message.error('Failed to upload drawing')
      } finally {
        setUploadingDrawing(false)
      }
    },
    [
      selectedComponent,
      metadataEntryId,
      rolodexUploadFile,
      forceRefreshAssetScreenshots,
    ]
  )

  const handleDeleteDrawing = useCallback(
    async (drawing: ComponentDrawing) => {
      setDeletingDrawingId(drawing.componentId as ComponentId)
      try {
        await rolodexDeleteFile(metadataEntryId, drawing.imagePath)
        message.success('Drawing deleted successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to delete drawing:', error)
        message.error('Failed to delete drawing')
      } finally {
        setDeletingDrawingId(null)
      }
    },
    [metadataEntryId, rolodexDeleteFile, forceRefreshAssetScreenshots]
  )

  const missingComponents = useMemo(() => {
    const screenshotComponentIds = new Set(
      existingScreenshots.map((s) => s.componentId)
    )
    return (
      selectedComponents?.filter((c) => !screenshotComponentIds.has(c.id)) || []
    )
  }, [selectedComponents, existingScreenshots])

  const componentOptions = useMemo(() => {
    return (
      selectedComponents?.map((component) => ({
        label: component.data.name,
        value: component.id,
      })) || []
    )
  }, [selectedComponents])

  useInitializeDerivedCdfsFromRolodexData(
    cdfCache,
    CommercialFdeCdfSpecificationList,
    unit?.id ?? [],
    isInspectionThicknessCDF
  )

  const cdfOptions = useMemo(() => {
    return componentCDFs.map((cdf) => ({
      label: `${cdf.data.params.view.name}${cdf.data.params.aggregation.name ? ` (${cdf.data.params.aggregation.name})` : ''}`,
      value: cdf.id,
    }))
  }, [componentCDFs])

  return (
    <Container>
      <ComponentSelector>
        <Space orientation='vertical' style={{ width: '100%' }}>
          <AntText strong>Select Component to Capture:</AntText>
          <Select
            style={{ width: '100%' }}
            placeholder='Select a component'
            value={selectedComponentId}
            onChange={setSelectedComponentId}
            options={componentOptions}
          />
          {selectedComponent && cdfOptions.length > 0 && (
            <>
              <AntText strong>Select CDF:</AntText>
              <Select
                style={{ width: '100%' }}
                placeholder='Select a CDF'
                value={selectedCDFId}
                onChange={setSelectedCDFId}
                options={cdfOptions}
              />
            </>
          )}
          <Space orientation='horizontal' align='center'>
            <AntText>Screenshot Zoom Factor:</AntText>
            <InputNumber
              precision={2}
              value={zoomFactor}
              min={0}
              step={0.05}
              onChange={(value) => setZoomFactor(value as number)}
              style={{ width: '120px' }}
            />
            <AntText type='secondary' style={{ fontSize: '12px' }}>
              (Proportion of whitespace padding)
            </AntText>
          </Space>
        </Space>
      </ComponentSelector>

      {selectedComponent && assetVizRef && (
        <>
          <AssetVizCopy
            assetVizRef={assetVizRef}
            components={[selectedComponent]}
            componentDataFrames={relevantCDF ? [relevantCDF] : undefined}
            markupLayers={[]}
            assetModel={selectedAssetModel}
            getColorThresholdForComponent={() => colorThreshold}
            customMarkupLayers={[]}
            sceneConfig={assetViz.assetVizSceneConfig}
            mode={AssetVizMode.Screenshot}
            onSelection={setSelection}
            offscreenRender={true}
            showLegend={true}
          />

          <Space orientation='vertical' style={{ width: '100%' }}>
            <Button
              type='primary'
              onClick={captureComponentScreenshot}
              loading={loading}
              disabled={!selectedComponent}>
              Capture Screenshot
            </Button>

            <Upload
              accept='image/png,image/jpeg,image/jpg,image/gif,image/webp'
              beforeUpload={(file) => {
                handleDrawingUpload(file)
                return false
              }}
              showUploadList={false}
              maxCount={1}
              disabled={!selectedComponent}>
              <Button loading={uploadingDrawing} disabled={!selectedComponent}>
                Upload Asset Drawing
              </Button>
            </Upload>
          </Space>
        </>
      )}

      {missingComponents.length > 0 && (
        <Alert
          message='Missing Component Screenshots'
          description={
            <div>
              <p>The following components do not have screenshots yet:</p>
              <ul>
                {missingComponents.map((component) => (
                  <li key={component.id}>{component.data.name}</li>
                ))}
              </ul>
            </div>
          }
          type='warning'
          showIcon
        />
      )}

      <PreviewSection>
        <Typography.Title level={4}>Captured Screenshots</Typography.Title>
        {existingScreenshots.map((screenshot) => (
          <ImageCard
            key={screenshot.componentId}
            title={screenshot.componentName}>
            <AntImage
              src={screenshot.signedUrl}
              alt={screenshot.componentName}
              style={{ width: '100%', maxWidth: '600px' }}
            />
            <Popconfirm
              title='Delete screenshot?'
              description='This will remove the screenshot from the report.'
              okText='Delete'
              cancelText='Cancel'
              okButtonProps={{
                danger: true,
                loading: deletingComponentId === screenshot.componentId,
              }}
              onConfirm={() => handleDeleteScreenshot(screenshot)}>
              <Button
                danger
                loading={deletingComponentId === screenshot.componentId}
                style={{ marginTop: '0.75rem' }}>
                Delete Screenshot
              </Button>
            </Popconfirm>
          </ImageCard>
        ))}
      </PreviewSection>

      <PreviewSection>
        <Typography.Title level={4}>Uploaded Drawings</Typography.Title>
        {existingDrawings.length === 0 ? (
          <AntText type='secondary'>No drawings uploaded yet</AntText>
        ) : (
          existingDrawings.map((drawing) => (
            <ImageCard key={drawing.componentId} title={drawing.componentName}>
              <AntImage
                src={drawing.signedUrl}
                alt={drawing.componentName}
                style={{ width: '100%', maxWidth: '600px' }}
              />
              <Popconfirm
                title='Delete drawing?'
                description='This will remove the drawing from the report.'
                okText='Delete'
                cancelText='Cancel'
                okButtonProps={{
                  danger: true,
                  loading: deletingDrawingId === drawing.componentId,
                }}
                onConfirm={() => handleDeleteDrawing(drawing)}>
                <Button
                  danger
                  loading={deletingDrawingId === drawing.componentId}
                  style={{ marginTop: '0.75rem' }}>
                  Delete Drawing
                </Button>
              </Popconfirm>
            </ImageCard>
          ))
        )}
      </PreviewSection>
    </Container>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, selectedComponents, assetScreenshots } = useRugReportOps()

  const screenshots = useMemo(() => {
    const shots: ComponentScreenshot[] = []

    if (assetScreenshots?.fullComponentHeatmap) {
      assetScreenshots.fullComponentHeatmap.forEach((screenshot) => {
        if (screenshot.signedUrl) {
          const component = selectedComponents?.find(
            (c) => c.id === screenshot.componentId
          )
          if (component) {
            shots.push({
              componentId: screenshot.componentId,
              componentName: component.data.name,
              signedUrl: screenshot.signedUrl,
              imagePath: screenshot.imagePath,
            })
          }
        }
      })
    }

    return shots
  }, [assetScreenshots, selectedComponents])

  const drawings = useMemo(() => {
    const drwgs: ComponentDrawing[] = []

    if (assetScreenshots?.fullComponentDrawing) {
      assetScreenshots.fullComponentDrawing.forEach((drawing) => {
        if (drawing.signedUrl) {
          const component = selectedComponents?.find(
            (c) => c.id === drawing.componentId
          )
          if (component) {
            drwgs.push({
              componentId: drawing.componentId,
              componentName: component.data.name,
              signedUrl: drawing.signedUrl,
              imagePath: drawing.imagePath,
              extension: drawing.extension,
            })
          }
        }
      })
    }

    return drwgs
  }, [assetScreenshots, selectedComponents])

  const drawingsByComponentId = useMemo(() => {
    return associateBy({
      arr: drawings,
      key: 'componentId',
    })
  }, [drawings])

  const screenshotsByComponentId = useMemo(() => {
    return associateBy({
      arr: screenshots,
      key: 'componentId',
    })
  }, [screenshots])

  const componentsWithMedia = useMemo(() => {
    if (!selectedComponents) return []
    return selectedComponents.filter(
      (component) =>
        drawingsByComponentId.has(component.id) ||
        screenshotsByComponentId.has(component.id)
    )
  }, [selectedComponents, drawingsByComponentId, screenshotsByComponentId])

  if (componentsWithMedia.length === 0) return null

  let isFirstPage = true

  return (
    <>
      {componentsWithMedia.map((component) => {
        const drawing = drawingsByComponentId.get(component.id)
        const screenshot = screenshotsByComponentId.get(component.id)
        const pages = []

        if (drawing) {
          const tracker = isFirstPage
            ? {
                sectionId: RugReportSectionId.DATA_OVERVIEW,
                onRender,
              }
            : undefined

          pages.push(
            <ReportPage
              key={`drawing-${drawing.componentId}`}
              id={isFirstPage ? 'data-overview' : undefined}
              size='LETTER'
              orientation='landscape'
              pageStyle={pageStyles.imagePage}
              contentStyle={{ margin: 0 }}
              tracker={tracker}
              header={<PageHeader title='Data Overview' />}
              footer={
                <PageFooter
                  jobNumber={formData.mainInfo.assetId}
                  revisionNumber={formData.revisionMetadata.revisionNumber}
                />
              }>
              <ImagePageHeader
                componentId={drawing.componentId as ComponentId}
              />
              <PageSubheader
                title='Asset Drawing'
                subtitle={drawing.componentName}
              />
              <Image
                src={drawing.signedUrl}
                cache={false}
                style={pageStyles.pageImage}
              />
            </ReportPage>
          )
          isFirstPage = false
        }

        if (screenshot) {
          const tracker = isFirstPage
            ? {
                sectionId: RugReportSectionId.DATA_OVERVIEW,
                onRender,
              }
            : undefined

          pages.push(
            <ReportPage
              key={`screenshot-${screenshot.componentId}`}
              id={isFirstPage ? 'data-overview' : undefined}
              size='LETTER'
              orientation='landscape'
              pageStyle={pageStyles.imagePage}
              contentStyle={{ margin: 0 }}
              tracker={tracker}
              header={<PageHeader title='Data Overview' />}
              footer={
                <PageFooter
                  jobNumber={formData.mainInfo.assetId}
                  revisionNumber={formData.revisionMetadata.revisionNumber}
                />
              }>
              <ImagePageHeader
                componentId={screenshot.componentId as ComponentId}
              />
              <PageSubheader
                title='C-Scan Overview'
                subtitle={screenshot.componentName}
              />
              <Image
                src={screenshot.signedUrl}
                cache={false}
                style={pageStyles.pageImage}
              />
            </ReportPage>
          )
          isFirstPage = false
        }

        return pages
      })}
      <AddendumPages
        sectionId={RugReportSectionId.DATA_OVERVIEW}
        sectionTitle='Data Overview'
      />
    </>
  )
}

export const DataOverviewSection: ReportSection = {
  id: RugReportSectionId.DATA_OVERVIEW,
  title: 'Data Overview',
  description: 'Component thickness visualizations',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
